<div class="system-page">
  <div class="system-page__group">
    <a class="btn btn--basic" href="javascript:void(0)" (click)="backToObject()">&larr; Back to record</a>
  </div>

  <div class="system-page__group">
    <h3 class="subheading">Record</h3>
    @if (objectLoading) {
      <p>Loading...</p>
    }
    @if (objectError) {
      <p>Could not load this record.</p>
    }
    @if (!objectLoading && !objectError && dataObject) {
      <table class="status-table">
        <tbody>
          <tr>
            <td>Name</td>
            <td>{{ dataObject.name }}</td>
          </tr>
          <tr>
            <td>Types</td>
            <td>{{ types.join(', ') }}</td>
          </tr>
          <tr>
            <td>Id</td>
            <td>{{ id }}</td>
          </tr>
          @if (dataObject['_creationDate']) {
            <tr>
              <td>Created</td>
              <td>{{ appService.dateTimeToString(dataObject['_creationDate']) }}</td>
            </tr>
          }
          @if (dataObject['_modificationDate']) {
            <tr>
              <td>Modified</td>
              <td>{{ appService.dateTimeToString(dataObject['_modificationDate']) }}</td>
            </tr>
          }
          @if (relations.length > 0) {
            <tr>
              <td>Relations</td>
              <td>
                <ul class="plain-list">
                  @for (rel of relations; track rel.key) {
                    <li>
                      <strong>{{ rel.key }}</strong>:
                      @if (rel.link.values && rel.link.values.length > 0) {
                        @for (v of rel.link.values; track v.href; let last = $last) {
                          <a [routerLink]="v.href">{{ v.name }}</a>{{ last ? '' : ', ' }}
                        }
                      } @else {
                        <a [routerLink]="rel.link.href">{{ rel.link.name }}</a>
                      }
                    </li>
                  }
                </ul>
              </td>
            </tr>
          }
        </tbody>
      </table>
    }
  </div>

  <div class="system-page__group">
    <h3 class="subheading">Access</h3>
    <p>
      Below are the access control rules matching this record and their associated user roles. Operations are
      what each <strong>access-model role</strong> can do, not what any one person can do: the coarse viewer role
      is assigned per user alongside their access-model role and blocks every mutation at the route, so two
      holders of the same role can end up with different rights. This page never computes an "effective" set; it
      reports the rule's operations and each role's operations side by side.
    </p>

    @if (accessLoading) {
      <p>Loading...</p>
    }
    @if (accessError) {
      <p>Could not load access information for this record.</p>
    }

    @if (!accessLoading && !accessError && access) {
      @if (access.unresolved.length > 0) {
        <div class="system-page__confirm">
          <p class="system-page__confirm-text">
            {{ access.unresolved.length }} ACL {{ access.unresolved.length === 1 ? 'row' : 'rows' }} on this record
            point at a node the access model no longer has — the visible symptom of a definition that was renamed
            or removed without a rebuild. These rows still grant read, but never write, create or delete.
          </p>
          <table class="status-table">
            <thead>
              <tr>
                <th>AC id</th>
                <th>Node id</th>
              </tr>
            </thead>
            <tbody>
              @for (row of access.unresolved; track row.nodeId) {
                <tr class="status-table__row--warning">
                  <td>{{ row.acId }}</td>
                  <td>{{ row.nodeId }}</td>
                </tr>
              }
            </tbody>
          </table>
          <div class="system-page__confirm-actions">
            <a class="btn btn--basic" [class.btn--disabled]="rebuilding" href="javascript:void(0)" (click)="!rebuilding && rebuildAccessControl()">
              {{ rebuilding ? 'Rebuilding…' : 'Rebuild access control' }}
            </a>
          </div>
        </div>
      }

      @if (access.grants.length === 0 && access.unresolved.length === 0) {
        <p>No access control rule currently matches this record.</p>
      }

      @for (grant of access.grants; track grant.nodeId) {
        <div class="access-grant">
          <h4 class="access-grant__title">{{ grant.definition }}</h4>
          <ul class="access-grant__meta">
            <li><strong>Rooted on:</strong> {{ grant.nodeClass }}</li>
            <li><strong>Rule grants (objectAllows):</strong> {{ grant.objectAllows.join(', ') }}</li>
          </ul>

          <h5 class="access-grant__subheading">Roles</h5>
          @if (grant.roles.length > 0) {
            <ul class="plain-list access-grant__roles">
              @for (roleGrant of grant.roles; track roleGrant.role) {
                <li>{{ roleGrant.role }}{{ roleGrant.roleGrants ? ' (' + roleGrant.roleGrants.join(', ') + ')' : '' }}</li>
              }
            </ul>
          } @else {
            <p>No role currently holds this identifier.</p>
          }

          @if (grant.holders) {
            <h5 class="access-grant__subheading">Holders</h5>
            <table class="status-table access-grant__holders">
              <thead>
                <tr>
                  <th>Holder</th>
                  <th>Reached via</th>
                  <th>Account</th>
                  <th>Role match</th>
                </tr>
              </thead>
              <tbody>
                @for (holder of grant.holders; track holder.holderId) {
                  <tr>
                    <td>{{ holder.holderName }}</td>
                    <td>{{ holder.stepPath }}</td>
                    <td>{{ holder.accountId ? holder.accountId : 'no account' }}</td>
                    <td>
                      @if (holder.hasRole) {
                        {{ holder.roles.join(', ') }}
                      } @else {
                        no referencing role — does not see this grant
                      }
                    </td>
                  </tr>
                }
                @if (grant.holders.length === 0) {
                  <tr>
                    <td colspan="4">No one currently holds this identifier.</td>
                  </tr>
                }
              </tbody>
            </table>
          }
        </div>
      }
    }
  </div>
</div>
